// src/components/crm/CustomerQuotesView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { formatCurrency, formatDate } from '../../utils/formatters';
import QuoteStatusBadge from '../common/QuoteStatusBadge';
import { Plus, ArrowUp, ArrowDown } from 'lucide-react';
import QuoteDetailsView from '../../views/QuoteDetailsView';
import {
    resolveDocTerminology, // r1-p1-docterm-cqv-import-8f2b4d16
    // qexp-6f2a9d38-list — Seam 5: the ONE derived-expiry rule
    resolveExpiryState, resolveExpiryWarningDays,
    isQuoteArchived, // estdel-f9ac8ba8
} from '../../utils/quoteSchema';
import { can, PERMISSIONS } from '../../utils/permissions'; // quotelock-d5c9a2f6

const CustomerQuotesView = ({ customer, currentUserProfile, openQuoteModal, companySettings, onConvertToInvoice, onConvertToJob, onViewJob }) => { // onViewJob: CONVERT-FIX r1-cvt-cqv-prop-3e9a7c52
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuote, setSelectedQuote] = useState(null);
    
    // --- 1. Sorting State (Preserved) ---
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    useEffect(() => {
        if (!customer?.id || !currentUserProfile?.organizationId) return;

        setLoading(true);
        const orgId = currentUserProfile.organizationId;
        const quotesQuery = query(
            collection(db, `organizations/${orgId}/quotes`),
            where('customerId', '==', customer.id)
            // The initial orderBy from Firestore is still useful for the default fetch
        );

        const unsubscribe = onSnapshot(quotesQuery, (snapshot) => {
            const quotesData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate(),
                    expiresAt: data.expiresAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate(),
                    // Ensure total is a number for sorting
                    total: Number(data.total || 0) 
                };
            });
            setQuotes(quotesData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching customer quotes: ", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [customer, currentUserProfile]);

    // --- 2. Sorting Handler (Preserved) ---
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    
    // --- 3. Sorting Memoization (Preserved) ---
    const sortedQuotes = useMemo(() => {
        // estdel-f9ac8ba8 — deleted (hidden) estimates leave this tab; `quotes` keeps them so an estimate
        // deleted while open here still shows its banner (liveQuote below).
        let sortableItems = quotes.filter((q) => !isQuoteArchived(q));
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [quotes, sortConfig]);

    // --- 4. Sort Indicator Helper (Preserved) ---
    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending' 
            ? <ArrowUp className="inline-block ml-1 h-4 w-4" /> 
            : <ArrowDown className="inline-block ml-1 h-4 w-4" />;
    };


    if (selectedQuote) {
        // CONVERT-FIX VERIFIER: r1-cvt-cqv-livequote-6d3b8f29
        // Re-derive the open quote from the live `quotes` array (kept current
        // by the onSnapshot listener above) instead of using the frozen
        // selectedQuote captured at click time. This makes convertedToJobId
        // (stamped asynchronously by the onJobCreated backend trigger) flow
        // through to QuoteDetailsView in real time, so its "Convert to Job"
        // button flips to "View Job" without the user closing and reopening
        // the quote. Falls back to selectedQuote if the id isn't found yet.
        const liveQuote = quotes.find(q => q.id === selectedQuote.id) || selectedQuote;
        return (
            <QuoteDetailsView 
                quote={liveQuote}
                companySettings={companySettings}
                onBack={() => setSelectedQuote(null)}
                onEditQuote={openQuoteModal}
                onConvertToInvoice={onConvertToInvoice}
                onConvertToJob={onConvertToJob}
                onViewJob={onViewJob}
                currentUserProfile={currentUserProfile} // estdel-f9ac8ba8 — the Delete button / Restore need the viewer's role
            />
        );
    }
    
    // r1-p1-docterm-cqv-term-3a7e9c52 — org-default terminology for this customer's list labels
    const term = resolveDocTerminology(companySettings?.quoteTerminologyDefault);

    return (
        <div>
            {/* quotelock-d5c9a2f6 -- creating an estimate is MANAGE_FINANCIALS, which a
                Field Technician does not hold. Defence at the leaf so this component is
                safe wherever it is mounted, behind the CustomerFormModal tab gate and the
                CrmApp openQuoteModal/mount guards. */}
            {can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS) && (
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => openQuoteModal({ customer: customer })}
                    className="flex items-center p-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                >
                    <Plus size={16} className="mr-2" />
                    Create {term.word}{/* r1-p1-docterm-cqv-btn-6d1f8b47 */}
                </button>
            </div>
            )}
            <div className="mt-6 bg-white shadow-sm rounded-lg overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        {/* --- 5. Sortable Headers (Preserved) --- */}
                        <tr>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('quoteNumber')}>
                               <div className="flex items-center">{term.word} # {getSortIndicator('quoteNumber')}</div>{/* r1-p1-docterm-cqv-col-2e5a9d38 */}
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('createdAt')}>
                               <div className="flex items-center">Date {getSortIndicator('createdAt')}</div>
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('total')}>
                               <div className="flex items-center">Total {getSortIndicator('total')}</div>
                            </th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('status')}>
                               <div className="flex items-center">Status {getSortIndicator('status')}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" className="text-center py-10">Loading {term.wordPlural.toLowerCase()}...{/* r1-p1-docterm-cqv-loading-9b4c1e63 */}</td></tr>
                        // --- 6. Mapping Sorted Quotes (Preserved) ---
                        ) : sortedQuotes.length > 0 ? sortedQuotes.map(quote => (
                            <tr
                                key={quote.id}
                                className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                                onClick={() => setSelectedQuote(quote)}
                            >
                                <td className="px-6 py-4 font-medium text-blue-600 hover:underline">{quote.quoteNumber}</td>
                                <td className="px-6 py-4">{formatDate(quote.createdAt)}</td>
                                <td className="px-6 py-4">{formatCurrency(quote.total)}</td>
                                <td className="px-6 py-4"><QuoteStatusBadge status={quote.status} isConverted={!!quote.convertedToJobId}
                                    /* qexp-6f2a9d38-list — expiresAt was already read into row state here and dropped; no extra query. */
                                    expiryState={resolveExpiryState(quote, { timeZone: companySettings?.timezone, warningDays: resolveExpiryWarningDays(companySettings?.quoteDefaults) }).state} /></td>
                            </tr>
                        )) : (
                            <tr><td colSpan="4" className="text-center py-10 text-gray-500">No {term.wordPlural.toLowerCase()} found for this customer.{/* r1-p1-docterm-cqv-empty-4f7b2c95 */}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomerQuotesView;